
import { httpsCallable } from "firebase/functions";
import { computed, defineComponent, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { functions } from "@/firebase";
import { useVaultStore } from "@/stores/vault";
import WithdrawalForm, {
  type WithdrawalFormValue,
  type WithdrawalFormSubmitPayload,
} from "./WithdrawalForm.vue";

export default defineComponent({
  name: "create-withdrawal",
  components: { WithdrawalForm },
  setup() {
    const vault = useVaultStore();
    const { summaryAvailable, summaryBtcPrice } = storeToRefs(vault);

    const creating = ref(false);
    const formValue = ref<WithdrawalFormValue | null>(null);
    const formResetKey = ref(0);

    const availableBtc = computed(
      () => parseFloat(String(summaryAvailable.value ?? "0")) || 0
    );

    const wakeIsValidWallet = async () => {
      try {
        const wakeFn = httpsCallable(functions, "Payments-isValidWallet");
        await wakeFn("wake-up");
      } catch (error) {
        // silent wake-up failure
      }
    };

    const validateAddress = async (address: string): Promise<boolean> => {
      try {
        const validateFn = httpsCallable(functions, "Payments-isValidWallet");
        const res: any = await validateFn(address);
        const opcode = res?.data?.OPCODE || res?.OPCODE;
        const payload = res?.data ?? res;
        const validFlag =
          opcode === "SUCCESS"
            ? payload?.data?.valid ?? payload?.valid ?? true
            : payload?.valid ?? payload === true;
        return Boolean(validFlag);
      } catch (error) {
        console.error("Error validating address", error);
        return false;
      }
    };

    const submitTransaction = async (payload: WithdrawalFormSubmitPayload) => {
      if (
        payload.totalBtc > 0 &&
        availableBtc.value > 0 &&
        payload.totalBtc > availableBtc.value
      ) {
        const confirmContinue = confirm(
          "El monto total excede el disponible. ¿Deseas continuar de todos modos?"
        );
        if (!confirmContinue) return;
      }

      creating.value = true;
      try {
        const createFn = httpsCallable(
          functions,
          "Vault-CreateSystemWithdrawalRequest"
        );
        const result = await createFn({
          destinations: payload.destinations,
          concept: payload.concept,
          note: payload.note,
        });
        const response: any = result.data;
        if (response?.OPCODE === "SUCCESS") {
          const createdId = response?.data?.id || response?.data?.requestId;
          const msg = createdId
            ? `Retiro creado correctamente (ID: ${createdId})`
            : "Retiro creado correctamente";
          alert(msg);
          formValue.value = { concept: "", note: "", destinations: [] };
          formResetKey.value += 1;
        } else {
          const message =
            response?.message ||
            response?.data?.message ||
            "No se pudo crear el retiro";
          alert(message);
        }
      } catch (error: any) {
        console.error("Error creating withdrawal", error);
        alert(error?.message || "Error al crear el retiro");
      } finally {
        creating.value = false;
      }
    };

    onMounted(() => {
      wakeIsValidWallet();
      vault.fetchSummary();
    });

    return {
      creating,
      formValue,
      formResetKey,
      availableBtc,
      summaryBtcPrice,
      validateAddress,
      submitTransaction,
    };
  },
});
